import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Info, Loader2, Save } from 'lucide-react';
import { useState } from 'react';
import { useForm, type Control, type FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuthStore } from '@/auth/store';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EquipmentPicker } from '@/components/ui/equipment-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { VesselSelect } from '@/components/ui/vessel-select';
import { useCreateCertificate } from '@/features/certificates/queries';
import type { CreateCertificateInput } from '@/features/certificates/schema';
import {
  CERTIFICATE_ISSUERS,
  type CertificateScope,
} from '@/features/certificates/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Flat form schema. The wire payload is a discriminated union on
 * `scope` (createCertificateSchema in features/certificates/schema.ts);
 * react-hook-form is much friendlier with a flat shape, so we
 * validate parent-required-by-scope via superRefine and rebuild the
 * discriminated payload at submit time.
 */
const isoDateString = z
  .string()
  .min(1, 'Required')
  .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Invalid date' });

const optionalString = z
  .string()
  .trim()
  .optional()
  .or(z.literal('').transform(() => undefined));

const formSchema = z
  .object({
    scope: z.enum(['vessel', 'equipment']),
    vesselId: z.string().optional(),
    equipmentId: z.string().optional(),

    certificateNumber: z.string().trim().min(1, 'Required').max(200),
    title: z.string().trim().min(1, 'Required').max(300),

    issuer: z.enum(CERTIFICATE_ISSUERS),
    issuerName: z.string().trim().min(1, 'Required').max(200),
    issuingAuthority: optionalString,

    issuedAt: isoDateString,
    validFrom: isoDateString,
    expiresAt: isoDateString,

    supersedesCertificateId: optionalString,
    notes: optionalString,
  })
  .superRefine((data, ctx) => {
    if (data.scope === 'vessel' && !data.vesselId) {
      ctx.addIssue({
        code: 'custom',
        path: ['vesselId'],
        message: 'Required',
      });
    }
    if (data.scope === 'equipment' && !data.equipmentId) {
      ctx.addIssue({
        code: 'custom',
        path: ['equipmentId'],
        message: 'Required',
      });
    }
    if (
      data.validFrom &&
      data.expiresAt &&
      !Number.isNaN(Date.parse(data.validFrom)) &&
      !Number.isNaN(Date.parse(data.expiresAt)) &&
      Date.parse(data.expiresAt) <= Date.parse(data.validFrom)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['expiresAt'],
        message: 'Expiry must be after valid-from date',
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

/** Generic text/date field bound to react-hook-form. */
function TextField({
  control,
  name,
  label,
  hint,
  type = 'text',
  textarea = false,
  placeholder,
}: {
  control: Control<FormValues>;
  name: FieldPath<FormValues>;
  label: string;
  hint?: string;
  type?: 'text' | 'date';
  textarea?: boolean;
  placeholder?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {textarea ? (
              <textarea
                {...field}
                rows={3}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                placeholder={placeholder}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={Boolean(fieldState.error)}
              />
            ) : (
              <Input
                type={type}
                {...field}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                placeholder={placeholder}
                aria-invalid={Boolean(fieldState.error)}
              />
            )}
          </FormControl>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function CertificateFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const createMut = useCreateCertificate();

  // Vessel filter for the equipment-scope branch — purely a UI helper
  // so the EquipmentPicker can fetch a vessel-scoped equipment list.
  // Not part of the wire payload (backend derives vesselId from
  // equipmentId server-side).
  const [equipmentScopeVesselId, setEquipmentScopeVesselId] = useState<
    string | null
  >(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      scope: 'vessel' as CertificateScope,
      vesselId: '',
      equipmentId: '',
      certificateNumber: '',
      title: '',
      issuer: 'classification_society',
      issuerName: '',
      issuingAuthority: '',
      issuedAt: '',
      validFrom: '',
      expiresAt: '',
      supersedesCertificateId: '',
      notes: '',
    },
  });

  const scope = form.watch('scope');

  const errorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('certificate.form.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('certificate.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    if (!user?.companyId) return;
    const common = {
      companyId: user.companyId,
      certificateNumber: values.certificateNumber,
      title: values.title,
      issuer: values.issuer,
      issuerName: values.issuerName,
      issuingAuthority: values.issuingAuthority || undefined,
      issuedAt: values.issuedAt,
      validFrom: values.validFrom,
      expiresAt: values.expiresAt,
      supersedesCertificateId: values.supersedesCertificateId || undefined,
      notes: values.notes || undefined,
    };
    const payload: CreateCertificateInput =
      values.scope === 'vessel'
        ? { ...common, scope: 'vessel', vesselId: values.vesselId as string }
        : {
            ...common,
            scope: 'equipment',
            equipmentId: values.equipmentId as string,
          };
    createMut.mutate(payload, {
      onSuccess: (created) => navigate(`/certificates/${created.id}`),
    });
  });

  if (!user?.companyId) {
    return (
      <div className="space-y-4">
        <header className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
            <Link to="/certificates">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">
            {t('certificate.form.createTitle')}
          </h1>
        </header>
        <Alert>
          <AlertTitle>{t('certificate.list.noCompanyTitle')}</AlertTitle>
          <AlertDescription>
            {t('certificate.list.noCompanyHint')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button
          asChild
          variant="ghost"
          size="icon"
          aria-label={t('common.back')}
        >
          <Link to="/certificates">
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">
          {t('certificate.form.createTitle')}
        </h1>
      </header>

      <Alert>
        <Info className="h-4 w-4" aria-hidden />
        <AlertDescription>{t('certificate.form.noEditHint')}</AlertDescription>
      </Alert>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('certificate.form.saveError')}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Scope + parent */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('certificate.form.sectionScope')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="scope"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('certificate.fields.scope')}</FormLabel>
                    <FormControl>
                      <div className="flex gap-2" role="radiogroup">
                        {(['vessel', 'equipment'] as const).map((s) => (
                          <button
                            key={s}
                            type="button"
                            role="radio"
                            aria-checked={field.value === s}
                            onClick={() => {
                              field.onChange(s);
                              // Reset the unused parent id so a stale
                              // value never sneaks into the payload.
                              if (s === 'vessel') {
                                form.setValue('equipmentId', '');
                                setEquipmentScopeVesselId(null);
                              } else {
                                form.setValue('vesselId', '');
                              }
                            }}
                            className={cn(
                              'rounded-md border px-3 py-2 text-sm font-medium',
                              field.value === s
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-input bg-background hover:bg-secondary',
                            )}
                          >
                            {t(`certificate.scope.${s}`)}
                          </button>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {scope === 'vessel' ? (
                <FormField
                  control={form.control}
                  name="vesselId"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('certificate.fields.vessel')}</FormLabel>
                      <FormControl>
                        <VesselSelect
                          value={field.value || null}
                          onChange={(v) => field.onChange(v ?? '')}
                          allowEmpty
                          emptyLabel={t('certificate.form.vesselPlaceholder')}
                          aria-invalid={Boolean(fieldState.error)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormItem>
                    <FormLabel>
                      {t('certificate.form.equipmentScopeVessel')}
                    </FormLabel>
                    <FormControl>
                      <VesselSelect
                        value={equipmentScopeVesselId}
                        onChange={(v) => {
                          setEquipmentScopeVesselId(v);
                          // Pivoting the vessel filter discards any
                          // previously-picked equipment from the
                          // other vessel.
                          form.setValue('equipmentId', '');
                        }}
                        allowEmpty
                        emptyLabel={t('certificate.form.vesselPlaceholder')}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {t('certificate.form.equipmentScopeVesselHint')}
                    </p>
                  </FormItem>
                  <FormField
                    control={form.control}
                    name="equipmentId"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>
                          {t('certificate.fields.equipment')}
                        </FormLabel>
                        <FormControl>
                          <EquipmentPicker
                            vesselId={equipmentScopeVesselId}
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? '')}
                            aria-invalid={Boolean(fieldState.error)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Identification */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('certificate.detail.sectionIdentity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                control={form.control}
                name="certificateNumber"
                label={t('certificate.fields.certificateNumber')}
                placeholder={t('certificate.form.certificateNumberPlaceholder')}
              />
              <TextField
                control={form.control}
                name="title"
                label={t('certificate.fields.title')}
                placeholder={t('certificate.form.titlePlaceholder')}
              />
              <FormField
                control={form.control}
                name="issuer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('certificate.fields.issuer')}</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {CERTIFICATE_ISSUERS.map((i) => (
                          <option key={i} value={i}>
                            {t(`certificate.issuer.${i}`)}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TextField
                control={form.control}
                name="issuerName"
                label={t('certificate.fields.issuerName')}
                placeholder={t('certificate.form.issuerNamePlaceholder')}
              />
              <div className="sm:col-span-2">
                <TextField
                  control={form.control}
                  name="issuingAuthority"
                  label={t('certificate.fields.issuingAuthority')}
                  hint={t('certificate.form.issuingAuthorityHint')}
                />
              </div>
              <div className="sm:col-span-2">
                <TextField
                  control={form.control}
                  name="supersedesCertificateId"
                  label={t('certificate.fields.supersedes')}
                  hint={t('certificate.form.supersedesHint')}
                />
              </div>
              <div className="sm:col-span-2">
                <TextField
                  control={form.control}
                  name="notes"
                  label={t('certificate.fields.notes')}
                  textarea
                />
              </div>
            </CardContent>
          </Card>

          {/* Validity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('certificate.detail.sectionValidity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <TextField
                control={form.control}
                name="issuedAt"
                label={t('certificate.fields.issuedAt')}
                type="date"
              />
              <TextField
                control={form.control}
                name="validFrom"
                label={t('certificate.fields.validFrom')}
                type="date"
              />
              <TextField
                control={form.control}
                name="expiresAt"
                label={t('certificate.fields.expiresAt')}
                type="date"
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" disabled={createMut.isPending}>
              <Link to="/certificates">{t('common.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Save aria-hidden />
              )}
              {t('certificate.form.submitCreate')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
